// 管理者認証(設計書 §3.5.1)
// 単一ステップ TOTP: email + password + totp_code を 1 リクエストで送信。
//
// セッション維持 / 期限切れ対応は performer 側 (AuthContext.tsx) と同じ仕組み:
// - heartbeat で access token を proactive refresh
// - visibilitychange で復帰時に再評価
// - apiRequest 経由の refresh 失敗 → /admin/login へ遷移

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, refreshSession } from '../api/client';
import { onAuthExpired } from '../lib/auth-events';
import { getTokenSecondsRemaining } from '../lib/jwt';
import { readJson, STORAGE_KEYS, writeJson } from '../lib/storage';

export interface AdminInfo {
  ulid: string;
  name: string;
}

interface LoginTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  admin: AdminInfo;
}

interface AdminAuthContextValue {
  admin: AdminInfo | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, totpCode: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null);

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ADMIN_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ADMIN);
}

const REFRESH_LEAD_SECONDS = 120; // 残り 2 分で refresh
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
const HEARTBEAT_RETRY_AFTER_MS = 30_000;

function computeRefreshDelayMs(token: string | null): number | null {
  const remaining = getTokenSecondsRemaining(token);
  if (remaining === null) return null;
  const delaySec = Math.max(0, remaining - REFRESH_LEAD_SECONDS);
  return Math.min(delaySec * 1000, MAX_TIMEOUT_MS);
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminInfo | null>(() =>
    readJson<AdminInfo>(STORAGE_KEYS.ADMIN),
  );
  const navigate = useNavigate();
  const heartbeatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [authTick, setAuthTick] = useState(0);
  const bumpAuthTick = useCallback(() => setAuthTick((n) => n + 1), []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) {
      clearTimeout(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string, totpCode: string) => {
      const data = await apiRequest<LoginTokens>('/admin/auth/login', {
        method: 'POST',
        body: { email, password, totp_code: totpCode },
      });
      localStorage.setItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN, data.access_token);
      localStorage.setItem(STORAGE_KEYS.ADMIN_REFRESH_TOKEN, data.refresh_token);
      writeJson(STORAGE_KEYS.ADMIN, data.admin);
      setAdmin(data.admin);
      bumpAuthTick();
    },
    [bumpAuthTick],
  );

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.ADMIN_REFRESH_TOKEN);
    if (refreshToken) {
      try {
        await apiRequest('/admin/auth/logout', {
          method: 'POST',
          body: { refresh_token: refreshToken },
        });
      } catch {
        // logout は best-effort
      }
    }
    clearTokens();
    setAdmin(null);
    stopHeartbeat();
    bumpAuthTick();
  }, [bumpAuthTick, stopHeartbeat]);

  // apiRequest 側から「セッション期限切れ」通知が来たら、performer と独立に
  // admin だけログアウトして /admin/login へ。
  useEffect(() => {
    const off = onAuthExpired('admin', () => {
      stopHeartbeat();
      setAdmin(null);
      bumpAuthTick();
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/admin/login')
      ) {
        navigate('/admin/login', { replace: true });
      }
    });
    return off;
  }, [bumpAuthTick, navigate, stopHeartbeat]);

  // Heartbeat
  useEffect(() => {
    if (!admin) {
      stopHeartbeat();
      return;
    }
    const accessToken = localStorage.getItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN);
    const delay = computeRefreshDelayMs(accessToken);
    if (delay === null) {
      void refreshSession('admin').then((newAccess) => {
        if (newAccess) bumpAuthTick();
      });
      return;
    }
    stopHeartbeat();
    heartbeatTimerRef.current = setTimeout(() => {
      heartbeatTimerRef.current = null;
      void refreshSession('admin').then((newAccess) => {
        if (newAccess) {
          bumpAuthTick();
        } else if (localStorage.getItem(STORAGE_KEYS.ADMIN_ACCESS_TOKEN)) {
          // refresh が一時失敗。ネットワーク blip を想定して短期リトライ。
          heartbeatTimerRef.current = setTimeout(() => {
            heartbeatTimerRef.current = null;
            bumpAuthTick();
          }, HEARTBEAT_RETRY_AFTER_MS);
        }
      });
    }, delay);
    return stopHeartbeat;
  }, [admin, authTick, bumpAuthTick, stopHeartbeat]);

  // visibilitychange による即時再評価
  useEffect(() => {
    function onVisibility() {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      bumpAuthTick();
    }
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [bumpAuthTick]);

  return (
    <AdminAuthContext.Provider
      value={{
        admin,
        isAuthenticated: admin !== null,
        login,
        logout,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used inside <AdminAuthProvider>');
  }
  return ctx;
}
